// ApexCharts configuration of the dashboard.

export const GENRE_COLORS = [
  "#27aae1",
  "#8b7cf6",
  "#22c58b",
  "#f5a524",
  "#ef5d8f",
  "#5b6475",
];

/**
 * Color of the genre at `index`: the palette first, then hues spread by the golden angle
 * so every genre keeps a distinct color however many there are.
 */
export const genreColor = (index) =>
  index < GENRE_COLORS.length
    ? GENRE_COLORS[index]
    : `hsl(${Math.round((index * 137.508) % 360)}, 62%, 58%)`;

/** Donut of views per genre, with the total in the middle. */
export function genreDonutChart(labels, values, totalLabel, colors = GENRE_COLORS) {
  return {
    options: {
      chart: { id: "dashboard-genres", background: "transparent" },
      colors,
      labels,
      dataLabels: { enabled: false },
      stroke: { show: false, width: 0 },
      legend: { show: false },
      tooltip: { theme: "dark" },
      plotOptions: {
        pie: {
          donut: {
            size: "72%",
            labels: {
              show: true,
              name: { color: "#9b9ba6", fontSize: "13px", offsetY: 18 },
              value: {
                color: "#f4f4f6",
                fontSize: "26px",
                fontWeight: 700,
                offsetY: -14,
              },
              total: {
                show: true,
                label: totalLabel,
                color: "#9b9ba6",
                fontSize: "13px",
              },
            },
          },
        },
      },
    },
    series: values,
  };
}

/**
 * Downloads of both stores: per day (area, `series` data as `{ x: timestamp, y }`, each
 * store on its own days) or per month (stacked bars on `categories`, data aligned on them).
 * `series`: [{ name, data, color }], `formatDate(timestamp)`: x labels of the daily view.
 */
export function downloadsChart(series, { monthly = false, categories = [], formatDate } = {}) {
  const muted = { colors: "#9b9ba6" };
  const days = new Set(series.flatMap((serie) => (monthly ? [] : serie.data.map((point) => point.x))));
  return {
    type: monthly ? "bar" : "area",
    options: {
      chart: {
        id: "dashboard-downloads",
        background: "transparent",
        stacked: monthly,
        toolbar: { show: false },
        zoom: { enabled: false },
        animations: { enabled: false },
        fontFamily: "inherit",
      },
      colors: series.map((serie) => serie.color),
      dataLabels: { enabled: false },
      stroke: monthly ? { width: 0 } : { curve: "smooth", width: 2.5 },
      fill: monthly
        ? { opacity: 0.9 }
        : { type: "solid", opacity: 0.15 },
      plotOptions: { bar: { columnWidth: "60%", borderRadius: 3 } },
      markers: { size: !monthly && days.size <= 10 ? 4 : 0, strokeWidth: 0, hover: { size: 5 } },
      grid: { borderColor: "rgba(255, 255, 255, 0.06)", strokeDashArray: 4 },
      legend: { show: true, position: "top", horizontalAlign: "right", labels: muted },
      xaxis: monthly
        ? {
            categories,
            tickAmount: Math.min(categories.length, 12),
            labels: { style: muted, rotate: 0, hideOverlappingLabels: true },
            axisBorder: { show: false },
            axisTicks: { show: false },
          }
        : {
            type: "datetime",
            labels: { style: muted, datetimeUTC: true, formatter: (value) => formatDate(value) },
            axisBorder: { show: false },
            axisTicks: { show: false },
          },
      yaxis: {
        min: 0,
        forceNiceScale: true,
        labels: { style: muted, formatter: (value) => Math.round(value).toLocaleString() },
      },
      tooltip: {
        theme: "dark",
        shared: true,
        intersect: false,
        ...(monthly ? {} : { x: { formatter: (value) => formatDate(value) } }),
      },
    },
    series: series.map(({ name, data }) => ({ name, data })),
  };
}
